import { matchPath } from 'react-router-dom';

import {
  PUBLIC_SEARCH,
  PUBLIC_LESSON_BASE,
  PUBLIC_UNIT_BASE,
  PUBLIC_TOPIC_BASE,
  PUBLIC_ASSESSMENT_BASE,
  PUBLIC_SCOPE_AND_SEQUENCE,
  PUBLIC_CONTACT_US,
  PUBLIC_STIMULUS,
  PUBLIC_SUBJECT_BASE,
  PUBLIC_SCHOOL_BENEFITS,
  PUBLIC_TEACHER_BENEFITS,
} from 'global/public-routes.constants';
import {
  PRIVATE_CLASS_TO_DO,
  PRIVATE_PLANNER,
  PRIVATE_ANNOTATIONS,
  PRIVATE_CLASSES_PARAM,
  PRIVATE_MY_STUDENTS,
  PRIVATE_ASSISTANT,
} from 'global/private-routes.constants';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import {
  EVENT_ACTION_FOLLOW,
  EVENT_ACTION_OPEN,
  EVENT_CATEGORY_NAV,
  EVENT_CATEGORY_SIDE_NAV_PRIMARY,
  EVENT_CATEGORY_SIDE_NAV_SECONDARY,
  EVENT_CATEGORY_TOP_NAV_MENU,
  EVENT_TARGET_TYPE_MENU,
  EVENT_TARGET_TYPE_NAV_ITEM,
} from 'approot/shared/data-tracker/data-events.constants';

export function isLessonActive(pathname: string) {
  const match = matchPath(
    {
      path: PRIVATE_CLASSES_PARAM,
    },
    pathname
  );
  let hasClassId = !isNaN(parseInt(match?.params?.userClassId || ''));
  return (
    hasClassId ||
    pathname.includes(PUBLIC_SEARCH) ||
    pathname.includes(PUBLIC_LESSON_BASE) ||
    pathname.includes(`${PUBLIC_UNIT_BASE}/`) ||
    pathname.includes(`${PUBLIC_TOPIC_BASE}/`) ||
    pathname.includes(PUBLIC_ASSESSMENT_BASE) ||
    pathname.includes(PUBLIC_SUBJECT_BASE) ||
    pathname.includes(PUBLIC_STIMULUS)
  );
}

export function isClassToDoActive(pathname: string) {
  return pathname.includes(PRIVATE_CLASS_TO_DO);
}

export function isScopeAndSequenceActive(pathname: string) {
  return pathname.includes(PUBLIC_SCOPE_AND_SEQUENCE);
}

export function isPlannerActive(pathname: string) {
  return pathname.includes(PRIVATE_PLANNER);
}

export function isStudentsActive(pathname: string) {
  return pathname.includes(PRIVATE_MY_STUDENTS);
}

export function isAnnotationsActive(pathname: string) {
  return pathname.includes(PRIVATE_ANNOTATIONS);
}

export function isContactUsActive(pathname: string) {
  return pathname.startsWith(PUBLIC_CONTACT_US);
}

export function isBenefitsActive(pathname: string) {
  return (
    pathname.includes(PUBLIC_TEACHER_BENEFITS) ||
    pathname.includes(PUBLIC_SCHOOL_BENEFITS)
  );
}

export function isAssistantActive(pathname: string) {
  return pathname.includes(PRIVATE_ASSISTANT);
}

export const SUBJECTS_LINK = 'Subjects';
export const DISCIPLINES_LINK = 'Disciplines';
export const PLANNER_LINK = 'Planner';
export const ANNOTATIONS_LINK = 'Annotations';
export const CLASS_TO_DO_LINK = 'Class To Do';
export const STUDENTS_LINK = 'Students';
export const ASSISTANT_LINK = 'AI Assistant';
export const JOIN_LINK = 'Join';
export const SIGN_IN_LINK = 'Sign In';

const SIDE_NAV_PRIMARY_LINKS = [
  SUBJECTS_LINK,
  DISCIPLINES_LINK,
  PLANNER_LINK,
  ANNOTATIONS_LINK,
  CLASS_TO_DO_LINK,
  STUDENTS_LINK,
  ASSISTANT_LINK,
  JOIN_LINK,
] as const;

export const TEACHING_BLOG_LINK = 'The Teaching Blog';
export const SCOPE_AND_SEQUENCE_LINK = 'Scope and Sequence';
export const CONTACT_US_LINK = 'Contact Us';

const SIDE_NAV_SECONDARY_LINKS = [
  TEACHING_BLOG_LINK,
  SCOPE_AND_SEQUENCE_LINK,
  CONTACT_US_LINK,
  SIGN_IN_LINK,
] as const;

type PrimaryLink = typeof SIDE_NAV_PRIMARY_LINKS[number];
type SecondaryLink = typeof SIDE_NAV_SECONDARY_LINKS[number];

export function pushSideNavFollowDataEvent(
  identifier: PrimaryLink | SecondaryLink
) {
  pushDataTrackerEvent(
    SIDE_NAV_PRIMARY_LINKS.includes(identifier as PrimaryLink)
      ? EVENT_CATEGORY_SIDE_NAV_PRIMARY
      : EVENT_CATEGORY_SIDE_NAV_SECONDARY,
    EVENT_ACTION_FOLLOW,
    {
      target_identifier: identifier,
      target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    }
  );
}

export function pushNavFollowDataEvent(label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_FOLLOW, {
    target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    target_identifier: label,
    nav_type: 'nav',
    nav_id: 'guest-top-nav',
  });
}

export function pushNavOpenDataEvent(label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_OPEN, {
    target_type: EVENT_TARGET_TYPE_MENU,
    target_identifier: `guest-top-nav-${label.toLowerCase()}-menu`,
  });
}

export function pushNavSublinkDataEvent(menu: string, label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_FOLLOW, {
    target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    target_identifier: label,
    nav_type: EVENT_TARGET_TYPE_MENU,
    nav_id: `guest-top-nav-${menu.toLowerCase().replace(/\s/g, '-')}-menu`,
  });
}

export function pushFooterNavOpenDataEvent(label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_OPEN, {
    target_type: EVENT_TARGET_TYPE_MENU,
    target_identifier: `footer-nav-${label.toLowerCase()}-menu`,
  });
}

export function pushFooterNavSublinkDataEvent(menu: string, label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_FOLLOW, {
    target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    target_identifier: label,
    nav_type: EVENT_TARGET_TYPE_MENU,
    nav_id: `footer-nav-${menu.toLowerCase().replace(/\s/g, '-')}-menu`,
  });
}

export function pushFAQDataEvent(label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_NAV, EVENT_ACTION_FOLLOW, {
    target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    target_identifier: label,
    nav_type: 'accordion',
    nav_id: 'guest-pricing-faq-accordion',
  });
}

export function pushUserTopNavDataEvent(label: string) {
  pushDataTrackerEvent(EVENT_CATEGORY_TOP_NAV_MENU, EVENT_ACTION_FOLLOW, {
    target_type: EVENT_TARGET_TYPE_NAV_ITEM,
    target_identifier: label,
  });
}
